import { Check } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'

import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'

import { ConfirmCompletion } from './ConfirmCompletion'

export interface DoneToggleProps {
  done: boolean
  onChange: (done: boolean) => void
  title: string
  className?: string
}

/**
 * Casilla de completado. El estado se lee tambien por el `aria-checked`.
 *
 * **Terminar pide confirmación** (`ConfirmCompletion`); reabrir no. Vive acá y no
 * en cada pantalla para que la fila, la tarjeta y el panel se comporten igual.
 */
export function DoneToggle({ done, onChange, title, className }: DoneToggleProps) {
  const t = useT()
  const casilla = useRef<HTMLButtonElement>(null)
  const [confirmando, setConfirmando] = useState(false)
  const cancelar = useCallback(() => {
    setConfirmando(false)
    casilla.current?.focus()
  }, [])

  return (
    <>
      <button
        ref={casilla}
        type="button"
        role="checkbox"
        aria-checked={done}
        aria-expanded={done ? undefined : confirmando}
        aria-label={t(done ? 'tasks.toggle.reopen' : 'tasks.toggle.complete', { tarea: title })}
        onClick={() => (done ? onChange(false) : setConfirmando((abierto) => !abierto))}
        className={cn(
          'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[var(--radius-control)]',
          'border transition-colors duration-[var(--motion-fast)]',
          done
            ? 'border-done bg-done/20 text-done'
            : confirmando
              ? 'border-signal bg-signal/15'
              : 'border-hairline hover:border-text-3',
          className,
        )}
      >
        {/* Sin marca cuando no esta hecha: una tilde apagada se lee como "hecha". */}
        {done ? <Check size={11} aria-hidden="true" /> : null}
      </button>

      <ConfirmCompletion
        anchor={casilla}
        open={confirmando}
        title={title}
        onCancel={cancelar}
        onConfirm={() => {
          setConfirmando(false)
          onChange(true)
        }}
      />
    </>
  )
}
